import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useMemo } from 'react';

import { MergeStrategy } from '@/features/pulls/services/pulls';
import { repositoryQuery } from '@/services/repo';

export function useMergeStrategies() {
  const { owner, repo } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });

  // Fetch repository details to get merge settings
  const { data: repository, isLoading } = useQuery(repositoryQuery(owner, repo));

  // Determine available merge strategies
  const availableStrategies = useMemo(() => {
    // If repository data isn't available yet, default to just 'merge'
    if (!repository) return ['merge'] as MergeStrategy[];

    // Extract repository settings with fallback to true for safety
    const allowMergeCommit = repository.allow_merge_commit ?? true;
    const allowSquashMerge = repository.allow_squash_merge ?? true;
    const allowRebaseMerge = repository.allow_rebase_merge ?? true;

    // Build array of allowed strategies
    const strategies: MergeStrategy[] = [];
    if (allowMergeCommit) strategies.push('merge');
    if (allowSquashMerge) strategies.push('squash');
    if (allowRebaseMerge) strategies.push('rebase');

    // Default to at least 'merge' if nothing is allowed (shouldn't happen)
    return strategies.length > 0 ? strategies : ['merge' as MergeStrategy];
  }, [repository]);

  // Get the first available strategy as default
  const defaultStrategy = availableStrategies[0];

  return {
    availableStrategies,
    defaultStrategy,
    isLoading,
    repository,
  };
}
